import { useCallback } from 'react'

import { useApolloClient, useQuery } from '@apollo/client/react'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

/**
 * Fragment for changelog entry preview (used in notification card)
 */
const ChangelogEntryPreview = graphql(`
  fragment ChangelogEntryPreview on ChangelogEntry {
    id
    title
    publishedAt
  }
`)

/**
 * Fragment for changelog entry with all display fields
 * Used in both list view cards and full entry dialog
 */
const ChangelogEntryDisplay = graphql(`
  fragment ChangelogEntryDisplay on ChangelogEntry {
    id
    title
    content
    coverImageUrl
    publishedAt
    audience
    slug
    author {
      name
      avatar
    }
  }
`)

const LatestChangelogEntryQuery = graphql(
  `
    query LatestChangelogEntry {
      changelogEntries(input: { first: 1 }) {
        edges {
          node {
            ...ChangelogEntryPreview
          }
        }
      }
    }
  `,
  [ChangelogEntryPreview],
)

const ChangelogEntryQuery = graphql(
  `
    query ChangelogEntry($input: ChangelogEntryInput!) {
      changelogEntry(input: $input) {
        changelogEntry {
          ...ChangelogEntryDisplay
        }
      }
    }
  `,
  [ChangelogEntryDisplay],
)

const ChangelogEntriesListQuery = graphql(
  `
    query ChangelogEntriesList($input: ChangelogEntriesInput!) {
      changelogEntries(input: $input) {
        edges {
          cursor
          node {
            ...ChangelogEntryDisplay
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          endCursor
        }
      }
    }
  `,
  [ChangelogEntryDisplay],
)

export type LatestChangelogEntry = ResultOf<typeof ChangelogEntryPreview>
export type ChangelogEntryData = ResultOf<typeof ChangelogEntryDisplay>
/**
 * A changelog entry's audience, derived from the `audience` selection.
 *
 * @see ChangelogEntryDisplay
 */
export type ChangelogEntryAudience = ChangelogEntryData['audience']
/** @deprecated Use ChangelogEntryData instead */
export type ChangelogEntryCardData = ChangelogEntryData
/** @deprecated Use ChangelogEntryData instead */
export type FullChangelogEntry = ChangelogEntryData

/**
 * Hook to fetch the latest changelog entry for the notification card
 */
export function useLatestChangelog(): {
  entry: LatestChangelogEntry | null
  loading: boolean
} {
  const { data, loading } = useQuery(LatestChangelogEntryQuery, {
    fetchPolicy: 'cache-first',
  })

  const node = data?.changelogEntries?.edges?.[0]?.node
  const entry = node ? readFragment(ChangelogEntryPreview, node) : null

  return {
    entry,
    loading,
  }
}

/**
 * Hook to fetch a single changelog entry with full content
 */
export function useChangelogEntry(id: string | null): {
  entry: FullChangelogEntry | null
  loading: boolean
  error: Error | undefined
} {
  const { data, loading, error } = useQuery(ChangelogEntryQuery, {
    variables: { input: { id: id ?? '' } },
    skip: !id,
    fetchPolicy: 'cache-first',
  })

  const node = data?.changelogEntry?.changelogEntry
  const entry = node ? readFragment(ChangelogEntryDisplay, node) : null

  return {
    entry,
    loading,
    error,
  }
}

/**
 * Hook to fetch a list of changelog entries for the list view
 */
export function useChangelogEntries(options?: { first?: number }): {
  entries: ChangelogEntryCardData[]
  loading: boolean
  error: Error | undefined
  hasMore: boolean
  loadMore: () => void
} {
  const { data, loading, error, fetchMore } = useQuery(
    ChangelogEntriesListQuery,
    {
      variables: { input: { first: options?.first ?? 20 } },
      fetchPolicy: 'cache-first',
    },
  )

  const edges = data?.changelogEntries?.edges ?? []
  const entries = edges
    .map((edge) =>
      edge?.node ? readFragment(ChangelogEntryDisplay, edge.node) : null,
    )
    .filter((entry): entry is ChangelogEntryData => entry !== null)

  const pageInfo = data?.changelogEntries?.pageInfo
  const hasMore = pageInfo?.hasNextPage ?? false

  const loadMore = (): void => {
    if (!hasMore || loading || !pageInfo) return
    void fetchMore({
      variables: {
        input: { first: options?.first ?? 20, after: pageInfo.endCursor },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.changelogEntries) return prev
        return {
          changelogEntries: {
            ...fetchMoreResult.changelogEntries,
            edges: [
              ...(prev.changelogEntries?.edges ?? []),
              ...fetchMoreResult.changelogEntries.edges,
            ],
          },
        }
      },
    })
  }

  return {
    entries,
    loading,
    error,
    hasMore,
    loadMore,
  }
}

/**
 * Picks the id of the most recent already-published general-audience entry.
 * Alpha/beta entries are skipped (they get their own badge) and future-dated
 * entries are excluded so the "New" badge never doubles up with "Scheduled".
 */
export function findLatestNewEntryId(
  entries: readonly ChangelogEntryData[],
): string | null {
  const now = Date.now()
  for (const entry of entries) {
    if (entry.audience === 'alpha' || entry.audience === 'beta') continue
    const publishedTime = new Date(entry.publishedAt).getTime()
    if (Number.isNaN(publishedTime) || publishedTime > now) continue
    return entry.id
  }
  return null
}

/**
 * Hook returning the id of the entry that should carry the "New" badge.
 * Reads from the cached list query so it doesn't trigger a network request
 * when the list has already been fetched.
 */
export function useLatestNewEntryId(): string | null {
  const { entries } = useChangelogEntries()
  return findLatestNewEntryId(entries)
}

const CreateChangelogEntryMutation = graphql(
  `
    mutation CreateChangelogEntry($input: CreateChangelogEntryInput!) {
      createChangelogEntry(input: $input) {
        __typename
        ... on ChangelogEntry {
          ...ChangelogEntryDisplay
        }
        ... on VoidMutationError {
          message
        }
      }
    }
  `,
  [ChangelogEntryDisplay],
)

/**
 * Hook to create a new changelog entry using useTypedMutation
 */
type CreateChangelogEntryHookResult = ReturnType<
  typeof useTypedMutation<
    'ChangelogEntry',
    ResultOf<typeof CreateChangelogEntryMutation>['createChangelogEntry'],
    ResultOf<typeof CreateChangelogEntryMutation>,
    VariablesOf<typeof CreateChangelogEntryMutation>
  >
>

export const useCreateChangelogEntry = (): CreateChangelogEntryHookResult =>
  useTypedMutation(
    CreateChangelogEntryMutation,
    (data) => data.createChangelogEntry,
    {
      successTypename: 'ChangelogEntry',
      refetchQueries: [
        { query: LatestChangelogEntryQuery },
        {
          query: ChangelogEntriesListQuery,
          variables: { input: { first: 20 } },
        },
      ],
    },
  )

const DeleteChangelogEntryMutation = graphql(`
  mutation DeleteChangelogEntry($input: DeleteChangelogEntryInput!) {
    deleteChangelogEntry(input: $input) {
      __typename
      ... on DeleteChangelogEntrySuccess {
        id
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

/**
 * Hook to delete a changelog entry using useTypedMutation
 */
type DeleteChangelogEntryHookResult = ReturnType<
  typeof useTypedMutation<
    'DeleteChangelogEntrySuccess',
    ResultOf<typeof DeleteChangelogEntryMutation>['deleteChangelogEntry'],
    ResultOf<typeof DeleteChangelogEntryMutation>,
    VariablesOf<typeof DeleteChangelogEntryMutation>
  >
>

export const useDeleteChangelogEntry = (): DeleteChangelogEntryHookResult =>
  useTypedMutation(
    DeleteChangelogEntryMutation,
    (data) => data.deleteChangelogEntry,
    {
      successTypename: 'DeleteChangelogEntrySuccess',
      refetchQueries: [
        { query: LatestChangelogEntryQuery },
        {
          query: ChangelogEntriesListQuery,
          variables: { input: { first: 20 } },
        },
      ],
    },
  )

const UpdateChangelogEntryMutation = graphql(
  `
    mutation UpdateChangelogEntry($input: UpdateChangelogEntryInput!) {
      updateChangelogEntry(input: $input) {
        __typename
        ... on ChangelogEntry {
          ...ChangelogEntryDisplay
        }
        ... on VoidMutationError {
          message
        }
      }
    }
  `,
  [ChangelogEntryDisplay],
)

/**
 * Hook to update a changelog entry using useTypedMutation
 */
type UpdateChangelogEntryHookResult = ReturnType<
  typeof useTypedMutation<
    'ChangelogEntry',
    ResultOf<typeof UpdateChangelogEntryMutation>['updateChangelogEntry'],
    ResultOf<typeof UpdateChangelogEntryMutation>,
    VariablesOf<typeof UpdateChangelogEntryMutation>
  >
>

export const useUpdateChangelogEntry = (): UpdateChangelogEntryHookResult =>
  useTypedMutation(
    UpdateChangelogEntryMutation,
    (data) => data.updateChangelogEntry,
    {
      successTypename: 'ChangelogEntry',
      refetchQueries: [
        { query: LatestChangelogEntryQuery },
        {
          query: ChangelogEntriesListQuery,
          variables: { input: { first: 20 } },
        },
      ],
    },
  )

/**
 * Fragment for changelog entry to display in notification
 * Includes coverImageUrl for the new card design
 */
const ChangelogEntryToDisplayFragment = graphql(`
  fragment ChangelogEntryToDisplay on ChangelogEntry {
    id
    title
    coverImageUrl
    audience
    publishedAt
    displayType
    summary
    ctas {
      id
      label
      url
    }
  }
`)

const ChangelogEntryToDisplayQuery = graphql(
  `
    query ChangelogEntryToDisplay {
      account {
        id
        changelogEntryToDisplay {
          ...ChangelogEntryToDisplay
        }
      }
    }
  `,
  [ChangelogEntryToDisplayFragment],
)

export type ChangelogEntryToDisplay = ResultOf<
  typeof ChangelogEntryToDisplayFragment
>

/**
 * Hook to fetch the changelog entry to display from the account
 * Returns null when user has seen all entries
 */
export function useChangelogEntryToDisplay(): {
  entry: ChangelogEntryToDisplay | null
  loading: boolean
} {
  const { data, loading } = useQuery(ChangelogEntryToDisplayQuery, {
    // cache-first (matching the sibling changelog queries) settles in a single
    // render; cache-and-network emitted twice, which flickered the animated
    // modal on load. markChangelogEntryAsSeen still refetches this query.
    fetchPolicy: 'cache-first',
  })

  const node = data?.account?.changelogEntryToDisplay
  const entry = node
    ? readFragment(ChangelogEntryToDisplayFragment, node)
    : null

  return {
    entry,
    loading,
  }
}

const MarkChangelogEntryAsSeenMutation = graphql(`
  mutation MarkChangelogEntryAsSeen($input: MarkChangelogEntryAsSeenInput!) {
    markChangelogEntryAsSeen(input: $input) {
      __typename
      ... on MarkChangelogEntryAsSeenSuccess {
        id
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

/**
 * Hook to mark a changelog entry as seen
 * Refetches the changelog entry to display query to update UI
 */
type MarkChangelogEntryAsSeenHookResult = ReturnType<
  typeof useTypedMutation<
    'MarkChangelogEntryAsSeenSuccess',
    ResultOf<
      typeof MarkChangelogEntryAsSeenMutation
    >['markChangelogEntryAsSeen'],
    ResultOf<typeof MarkChangelogEntryAsSeenMutation>,
    VariablesOf<typeof MarkChangelogEntryAsSeenMutation>
  >
>

export const useMarkChangelogEntryAsSeen =
  (): MarkChangelogEntryAsSeenHookResult =>
    useTypedMutation(
      MarkChangelogEntryAsSeenMutation,
      (data) => data.markChangelogEntryAsSeen,
      {
        successTypename: 'MarkChangelogEntryAsSeenSuccess',
        refetchQueries: [{ query: ChangelogEntryToDisplayQuery }],
      },
    )

/**
 * Hook to mark a changelog entry as seen, with an impersonation guard that
 * mirrors dismissable banners (`useDismissableBanner`).
 *
 * When an admin dismisses an announcement while impersonating a customer, the
 * dismissal must NOT persist to the real customer's account — otherwise the
 * customer never sees the announcement. In that case we only hide the entry for
 * the current session by clearing `account.changelogEntryToDisplay` in the
 * Apollo cache. Otherwise we fire the `markChangelogEntryAsSeen` mutation
 * exactly as before.
 */
export function useMarkChangelogEntrySeen(): (id: string) => void {
  const [markAsSeen] = useMarkChangelogEntryAsSeen()
  const { account } = useAccount()
  const apolloClient = useApolloClient()
  const isImpersonation = account?.isImpersonation === true

  return useCallback(
    (id: string): void => {
      if (isImpersonation) {
        // Hide the entry for this session only, without persisting to the
        // impersonated customer's account.
        const current = apolloClient.cache.readQuery({
          query: ChangelogEntryToDisplayQuery,
        })
        if (current?.account) {
          apolloClient.cache.writeQuery({
            query: ChangelogEntryToDisplayQuery,
            data: {
              account: {
                ...current.account,
                changelogEntryToDisplay: null,
              },
            },
          })
        }
        return
      }

      void markAsSeen({ variables: { input: { id } } })
    },
    [isImpersonation, apolloClient, markAsSeen],
  )
}
